// La lectura de «Mis ventas»: `GET /sales?index=seller` paginado en el servidor con filtros, orden y
// contadores. Es solo de esta pantalla; Home y «Mi tienda» siguen con `useRecentSales`.
import { useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { wizardApi } from '@/api/services/retail-wizard';
import type {
    SellerSalesCounters,
    SellerSalesFilterQuery,
} from '@/api/services/retail-wizard/contract';
import { PORTAL_KEYS, toRecentSales, type RecentSale } from '../../api/usePortal';

/** Filas por página del servidor, las mismas que Admin SK · Clientes. */
export const SELLER_SALES_PAGE_SIZE = 25;

const STALE_TIME_MS = 60_000;

const NO_ROWS: RecentSale[] = [];

/** Bajo `recentSales`: lo que refresca «Mis ventas» al llegar un aviso también alcanza a esta. */
const sellerSalesKey = (query: SellerSalesFilterQuery) =>
    [...PORTAL_KEYS.recentSales, 'paged', query] as const;

export interface SellerSalesResult {
    /** Las páginas aplanadas, con referencia estable entre renders. */
    rows: RecentSale[];
    /** Totales de las píldoras de la primera página; ausentes si el silo no los mandó. */
    counters: SellerSalesCounters | undefined;
    /** La primera página de ESTA consulta todavía viaja. */
    isPending: boolean;
    /** Falla de la primera página: sin nada cargado no hay tabla. */
    error: unknown;
    retry: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    /** Por qué falló la página siguiente; lo cargado se queda. */
    nextPageError: unknown;
}

export function useSellerSales(query: SellerSalesFilterQuery): SellerSalesResult {
    const result = useInfiniteQuery({
        queryKey: sellerSalesKey(query),
        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            wizardApi.listSellerSales({
                ...query,
                limit: SELLER_SALES_PAGE_SIZE,
                ...(pageParam === undefined ? { counters: true } : { nextToken: pageParam }),
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken ?? undefined,
        staleTime: STALE_TIME_MS,
        // Al cambiar el filtro la tabla anterior se queda hasta que llega la nueva, sin parpadeo.
        placeholderData: keepPreviousData,
    });

    // Memo obligatorio: TanStack Table compara `data` por referencia. El orden lo pone la pantalla.
    const rows = useMemo(
        () =>
            result.data ? toRecentSales(result.data.pages.flatMap((page) => page.items)) : NO_ROWS,
        [result.data],
    );

    return {
        rows,
        counters: result.data?.pages[0]?.counters,
        isPending: result.isPending,
        error: result.data ? null : result.error,
        retry: () => {
            void result.refetch();
        },
        hasNextPage: result.hasNextPage,
        isFetchingNextPage: result.isFetchingNextPage,
        fetchNextPage: () => {
            void result.fetchNextPage();
        },
        nextPageError: result.isFetchNextPageError ? result.error : null,
    };
}
